import { Section } from "@components/ui/Section";
import styles from "./page.module.css";
import { Heading } from "@components/ui/Heading";
import { LinkButton } from "@components/ui/LinkButton";
import { Card } from "@components/ui/Card";
import { Testimonial } from "@components/ui/Testimonial";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@components/ui/accordion";
import { useAuth0 } from "@auth0/auth0-react";
import { IndividualLoginAuth0Context } from "@providers";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";


export const Landing = () => {
  const { isAuthenticated, isLoading } = useAuth0(IndividualLoginAuth0Context);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || isAuthenticated) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <>
      <Section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <div className={styles.heroTextCol}>
            <Heading level={1} className={styles.headingFont}>
              Partner with Woolworths Group, all from one place
            </Heading>
            <div className={styles.heroSubtitle}>
              Introducing Woolworths MarketPlus, a new platform for businesses
              to both sell and manage products across Woolworths Group
              marketplaces
            </div>
            <LinkButton href="https://seller-forms.mydeal.com.au/mydealcomau/form/WMPSellerApplication/formperma/lBdNglYcopeb-2O2fv6q-aKGh-4GB6EWI6H1X6jKov4">
              Register your interest
            </LinkButton>
          </div>
          <img
            className={styles.heroImage}
            src="/market_plus_banner.png"
            itemProp="contentUrl"
            width="588"
            height="508"
            alt="Woolworths MarketPlus banner"
          />
        </div>
      </Section>
      <Section className={styles.sellersSection}>
        <Heading className={styles.sellersHeading} level={5}>
          Join hundreds of sellers
        </Heading>
        <div className={styles.sellersGrid}>
          <img
            src="/appliances-online-black-logo.png"
            className={styles.sellerLogo}
            itemProp="contentUrl"
            width="478"
            height="269"
            alt="appliances online"
          />
          <img
            src="/samsung-logo-black.svg"
            className={styles.sellerLogo}
            itemProp="contentUrl"
            width="850"
            height="400"
            alt="samsung"
          />
          <img
            src="/MobileCiti.png"
            className={styles.sellerLogo}
            itemProp="contentUrl"
            width="850"
            height="400"
            alt="mobileciti"
          />
          <img
            src="/gamesmen-logo-black.png"
            className={styles.sellerLogo}
            itemProp="contentUrl"
            width="640"
            height="320"
            alt="Gamesmen"
          />
        </div>
      </Section>
      <Section className={styles.benefitsSection}>
        <div className={styles.benefitsContainer}>
          <div className={styles.benefitsImageCol}>
            <img
              src="/vaccum.png"
              itemProp="contentUrl"
              width="1108"
              height="1068"
              alt="alt"
            />
            <span className={styles.benefitsNote}>
              Not real sales data. Product, pricing and sales information shown
              are illustrative only.
            </span>
          </div>
          <div className={styles.benefitsTextCol}>
            <Heading level={2} className={styles.benefitsTitle}>
              A better way to sell
            </Heading>
            <div className={styles.benefitsList}>
              <div className={styles.benefitItemTitle}>
                Value for customers via Everyday Rewards
              </div>
              <div className={styles.benefitItemDesc}>
                Customers can earn $10 off a future shop or convert to 1000
                Qantas points every time they reach 2000 Everyday Rewards points
              </div>
              <div className={styles.benefitItemTitle}>Reach more customers</div>
              <div className={styles.benefitItemDesc}>
                Partner with Australia’s most trusted brand that reaches over
                700 million online visits annually across our marketplaces
              </div>
              <div className={styles.benefitItemTitle}>Grow your sales</div>
              <div className={styles.benefitItemDesc}>
                Sell products on some of Australia’s largest e-Commerce
                platforms including BIG W, Woolworths &amp; Everyday Rewards
              </div>
            </div>
            <LinkButton href="https://seller-forms.mydeal.com.au/mydealcomau/form/WMPSellerApplication/formperma/lBdNglYcopeb-2O2fv6q-aKGh-4GB6EWI6H1X6jKov4">
              Register your interest
            </LinkButton>
          </div>
        </div>
      </Section>
      <Section className={styles.marketplacesSection}>
        <Heading className={styles.marketplacesHeading} level={2}>
          Our marketplaces
        </Heading>
        <div className={styles.marketplacesGrid}>
          <Card
            imageUrl="/BIGW Market.png"
            imageWidth={810}
            imageHeight={256}
            imageAlt="BigW Market"
            title="BIG W"
            titleLinkUrl="https://www.bigw.com.au/marketplace"
          >
            BIG W Market is an integrated online marketplace where customers can
            buy an extended range of products online only. We&#39;ve teamed up
            with trusted sellers, offering you more choice and more to love.
          </Card>
          <Card
            imageUrl="/Everyday Market.png"
            imageWidth={2160}
            imageHeight={256}
            imageAlt="Everyday Market"
            title="Everyday Market"
            titleLinkUrl="https://www.woolworths.com.au/shop/discover/everyday-market"
          >
            Everyday Market from Woolworths is an integrated online marketplace
            that enables customers to shop for an extended range of everyday
            items in one convenient place while you’re shopping for your
            groceries online.
          </Card>          
          <Card
            imageUrl="/wmp_ER_tile_810x480.png"
            imageWidth={810}
            imageHeight={256}
            imageAlt="Everyday Rewards"
            title="Everyday Rewards Shop"
            titleLinkUrl="https://www.everyday.com.au/shop.html"
          >
            The Everyday Rewards Shop is a loyalty store, exclusive to our
            members through the Everyday Rewards app. Members can easily earn
            points, use their Rewards dollars and shop a curated range of
            marketplace products.
          </Card>
        </div>
      </Section>
      <Section className={styles.testimonialsSection}>
        <Heading className={styles.testimonialsHeading} level={2}>
          Testimonials
        </Heading>
        <div className={styles.testimonialsGrid}>
          <Testimonial
            imageUrl="/EBGAMES.png"
            imageWidth={640}
            imageHeight={192}
            imageAlt="EB Games"
            quotee="Shane Stockwell - Managing Director"
          >
            Our partnership with Woolworths has smashed all expectations, within
            the first 6 months BigW is already our #2 marketplace and continues
            to grow. From the outset, their team has shown remarkable
            professionalism in all aspects. Be it technical integration through
            to promotional activity, our team at EB Games would work with them
            again any day.
          </Testimonial>
          <Testimonial
            imageUrl="/koh-logo.png"
            imageWidth={850}
            imageHeight={192}
            imageAlt="Koh"
            quotee="Ben Perceval - Head of eCommerce & CX"
          >
            We’re excited about the opportunity to drive exclusive special
            release kits, promotions and incentives to Woolworths customers.
            Utilising Everyday Market&#39;s reach to bring greater awareness and
            trial is one of our main objectives.
          </Testimonial>
          <Testimonial
            imageUrl="/new_aim_logo_updated.png"
            imageWidth={850}
            imageHeight={192}
            imageAlt="New Aim"
            quotee="Stephanie Ho General Manager - Channel & Category"
          >
            We are thrilled to be part of the Woolworths Group marketplaces. The
            access to their extensive customer database has empowered our brand
            with profound insights. The broad shopper coverage has exponentially
            expanded our reach across diverse demographics and regions. Most
            notably, aligning with the trusted Woolworths brand has elevated our
            credibility, opening doors to collaborations and unique
            opportunities.
          </Testimonial>
          <Testimonial
            imageUrl="/kg-group.png"
            imageWidth={850}
            imageHeight={192}
            imageAlt="KG Group"
            quotee="Doron Kushlin - Co-Founder"
          >
            Everyday Market has provided an incremental channel for acquisition
            and revenue. We have found the channel to be complementary to our
            store. Having an established brand like Woolworths endorse and range
            our brand drives credibility and trust for eCommerce brands. Being
            included in the Everyday Market email drove a surge in sales.
          </Testimonial>
        </div>
      </Section>
      <Section className={styles.faqSection}>
        <Heading className={styles.faqHeading} level={2}>
          FAQ
        </Heading>
        <Accordion type="multiple">
          <AccordionItem value="item-1">
            <AccordionTrigger>What is Woolworths MarketPlus?</AccordionTrigger>
            <AccordionContent>
              Woolworths MarketPlus is the bringing together of the Woolworths
              Group Marketplace businesses. It’s designed to make it easier for
              sellers to reach more customers in a streamlined way across the
              marketplace businesses making it easier for sellers to grow their
              business with Woolworths.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>
              How do I start selling with Woolworths MarketPlus?
            </AccordionTrigger>
            <AccordionContent>
              Complete the{" "}
              <a
                className={styles.faqLink}
                href="https://seller-forms.mydeal.com.au/mydealcomau/form/WMPSellerApplication/formperma/lBdNglYcopeb-2O2fv6q-aKGh-4GB6EWI6H1X6jKov4"
                target="_blank"
                rel="noopener noreferrer"
              >
                register your interest
              </a>{" "}
              form if you would like someone from our seller team to be in
              touch.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-3">
            <AccordionTrigger>
              How does this affect sellers who are already selling on Marketplaces within Woolworths Group?
            </AccordionTrigger>
            <AccordionContent>
              If you are currently selling on BIG W Market, Everyday Market from Woolworths &amp;
              Everyday Rewards Shop, there are no immediate changes. To ensure a
              top-quality shopping experience, Everyday Market and Big W Market
              carefully curate the brands and products we feature. If we believe
              your products would be a good fit, our team will reach out to you
              directly.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-4">
            <AccordionTrigger>
              What marketplaces does Woolworths MarketPlus give me access to?
            </AccordionTrigger>
            <AccordionContent>
              Woolworths Group has three Marketplaces. BIG W Market, Everyday Market from Woolworths 
               &amp; Everyday Rewards Shop.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-5">
            <AccordionTrigger>
              When will Woolworths MarketPlus be available?
            </AccordionTrigger>
            <AccordionContent>
              Our three marketplaces BIG W Market, Everyday Market from Woolworths &amp;
              Everyday Rewards Shop are live now. However they are curated
              marketplaces, which means we will be carefully selecting sellers,
              brands and products that are curated on these platforms. You can
              register your expression of interest by clicking the button above.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-6">
            <AccordionTrigger>
              I’m a customer and have questions about my marketplace order what
              should I do?
            </AccordionTrigger>
            <AccordionContent>
            
              For Everyday Market orders click{" "}
              <a
                className={styles.faqLink}
                href="https://www.woolworths.com.au/shop/page/help-and-support-faq?f=40"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
                <br />‍
              </a>
              For BIG W Market orders click{" "}
              <a
                className={styles.faqLink}
                href="https://www.bigw.com.au/my-account"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>{" "}
              to log into your account
              <br />
              For Everyday Rewards Shop orders click{" "}
              <a
                className={styles.faqLink}
                href="https://www.everyday.com.au/shop.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>{" "}
              to log into your account
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Section>
    </>
  );
};
